<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { cloudApi } from '@/api/index.js'
const props = defineProps({ tenantId: String, projectId: String, policy: Object })
const emit = defineEmits(['changed'])
const busy = ref(false), error = ref(''), notice = ref('')
let sequence = 0, request = null
const last = computed(() => props.policy?.recent_runs?.[0])
const canResume = computed(() => {
  const run = last.value
  if (!props.policy?.enabled || !run || !['failed', 'retry_wait'].includes(run.state) || run.created_count || run.existing_count) return false
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: props.policy.timezone || 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date())
  const day = Object.fromEntries(parts.map(p => [p.type, p.value]))
  return run.local_date === `${day.year}-${day.month}-${day.day}` && ['MATERIAL_NOT_ALLOWED', 'DISPATCH_SOURCE_NOT_READY', 'GENERATION_INPUT_MISSING'].includes(run.error_code)
})
function requestId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128
  const h = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20)}`
}
async function resume() {
  if (busy.value || !canResume.value) return
  if (!window.confirm('继续今天这次自动下发？系统会核对资料是否变化，按原规则准备内容并使用现有额度；已有任务不会重复创建。')) return
  const token = sequence
  busy.value = true; error.value = ''; notice.value = ''
  try {
    if (!request) request = { request_id: requestId(), expected_attempt: last.value.attempt }
    await cloudApi.post(`/api/tenants/${encodeURIComponent(props.tenantId)}/projects/${encodeURIComponent(props.projectId)}/daily-dispatch-runs/${encodeURIComponent(last.value.run_id)}:resume`, request)
    if (token !== sequence) return
    notice.value = '原运行已进入等待准备；手机接收和三图回执将分别更新。'; emit('changed')
  } catch (cause) {
    if (token === sequence) error.value = cause.status ? cause.message : '恢复结果尚未确认，请重新读取；再次确认会沿用同一次请求。'
  } finally { if (token === sequence) busy.value = false }
}
watch(() => JSON.stringify([props.tenantId, props.projectId, last.value?.run_id, last.value?.attempt]), () => {
  sequence++; request = null; busy.value = false; error.value = ''; notice.value = ''
})
onBeforeUnmount(() => { sequence++ })
</script>

<template>
  <section v-if="policy?.blocker || notice || error" class="run-recovery" aria-label="自动下发恢复">
    <p v-if="policy?.blocker" role="status">{{ policy.blocker.message }}</p>
    <p v-if="notice" role="status">{{ notice }}</p><p v-if="error" role="alert">{{ error }}</p>
    <button v-if="canResume" type="button" :disabled="busy" @click="resume">{{ busy ? '正在核对恢复…' : '恢复今天这次自动下发' }}</button>
    <p v-if="canResume">修正阻塞原因后继续原运行，保留原失败次数和记录。</p>
  </section>
</template>

<style scoped>
.run-recovery { display: grid; gap: 10px; padding: 16px; border: 1px solid #efc3b9; border-radius: 12px; background: #fff7f4; overflow-wrap: anywhere; }
.run-recovery p { margin: 0; }.run-recovery button { min-height: 44px; padding: 10px 14px; background: #08746d; color: white; border: 0; border-radius: 9px; }.run-recovery [role=alert] { color: #ad352b; }
</style>
